// src/lib/pollStorage.ts
// Utilitaires de stockage/local pour les sondages (mode développement localStorage)
// - Centralise l'accès à localStorage et la logique de duplication/suppression
// - Fournit aussi des helpers d'URL et de copie presse-papier

import {
  readFromStorage,
  writeToStorage,
  addToStorage,
  findById,
  updateInStorage,
  deleteFromStorage,
  readRecordStorage,
  writeRecordStorage,
} from "./storage/storageUtils";
import {
  handleError,
  logError,
  DooDatesError,
  ErrorSeverity,
  ErrorCategory,
} from "./error-handling";
import { logger } from "./logger";
import { getDeviceId } from "./guestIdentityService";

// ============================================================================
// EXPORTS & CONTEXT
// ============================================================================

// Re-export EVERYTHING from the new poll directory via barrel export
export * from "./poll";

// Explicitly import for use in this file
import type {
  Poll,
  FormResponse,
  Vote,
  FormResponseItem,
  FormQuestionShape,
  LegacyFormPoll,
  FormResults,
  DateQuestionResults,
} from "./poll";

import {
  isDatePoll,
  validatePoll,
  getPolls,
  getAllPolls as getRawAllPolls,
  savePolls,
  getAllVotes,
  saveVotes,
  readAllResponses,
  writeAllResponses,
  FORM_STORAGE_KEY,
  memoryPollCache,
  hasWindow,
} from "./poll";

// ============================================================================
// ORCHESTRATION FUNCTIONS (High-level business logic)
// ============================================================================

export function getPollBySlugOrId(
  idOrSlug: string | undefined | null,
): Poll | null {
  if (!idOrSlug) return null;
  const polls = getAllPolls();
  return (
    polls.find((p) => p.slug === idOrSlug) ||
    polls.find((p) => p.id === idOrSlug) ||
    null
  );
}

/**
 * @deprecated Use pollService.createPoll() directly or usePolls.createPoll() hook
 */
export async function addPoll(poll: Poll): Promise<string | null> {
  const normalizedPoll = { ...poll };

  if (
    isDatePoll(normalizedPoll) &&
    normalizedPoll.dates &&
    !normalizedPoll.settings?.selectedDates
  ) {
    normalizedPoll.settings = {
      ...normalizedPoll.settings,
      selectedDates: normalizedPoll.dates,
    };
  }

  const { enrichPollForGuest } = await import("@core/lib/guestIdentityService");
  const guestIds = await enrichPollForGuest(normalizedPoll);
  // Pour un guest, on stocke creator_id = deviceId localement (lecture client),
  // mais on envoie null à la RPC create_poll (qui exige p_creator_id NULL pour anon).
  // Le guest_fingerprint reste le canal d'identification côté serveur.
  const isGuest = !guestIds.creator_id;
  normalizedPoll.creator_id = String(guestIds.creator_id || getDeviceId());

  validatePoll(normalizedPoll);

  try {
    const { pollService } = await import("@core/services/PollService");

    // Passer TOUTES les données du poll, pas seulement un sous-ensemble
    const pollDataForService = {
      ...normalizedPoll, // Conserver toutes les propriétés (dates, questions, invitedEmails, etc.)
      type: normalizedPoll.type as "date" | "form" | "quizz" | "availability",
      title: normalizedPoll.title,
      description: normalizedPoll.description || "",
      slug: normalizedPoll.slug,
      creator_id: isGuest ? null : String(guestIds.creator_id),
      creator_type: (isGuest ? "guest" : "user") as "guest" | "user",
      guest_fingerprint: String(guestIds.guest_fingerprint || ""),
      status: normalizedPoll.status || "draft",
    };

    let createdPoll;
    try {
      createdPoll = await pollService.createPoll(pollDataForService);
    } catch (e) {
      const code = (e as { code?: string } | null)?.code;
      if (code === "SLUG_COLLISION") {
        // Poll likely already saved (stale state restored). Skip silently
        // to avoid spamming duplicates. Caller keeps current local copy.
        logger.warn(
          "addPoll: slug collision, poll likely already exists — skip",
          "poll",
          { slug: pollDataForService.slug, id: normalizedPoll.id },
        );
        return null;
      }
      throw e;
    }
    const polls = getRawAllPolls();
    const existingIndex = polls.findIndex((p) => p.id === createdPoll.id);

    if (existingIndex >= 0) {
      polls[existingIndex] = createdPoll as unknown as Poll;
    } else {
      polls.push(createdPoll as unknown as Poll);
    }
    savePolls(polls);

    memoryPollCache.set(createdPoll.id, createdPoll as unknown as Poll);

    const event = new CustomEvent("pollsChanged", {
      detail: { action: "create", pollId: createdPoll.id },
    });
    window.dispatchEvent(event);
    return createdPoll.id;
  } catch (error) {
    logger.warn("Supabase unavailable, fallback localStorage", "poll", {
      error,
    });
    const polls = getRawAllPolls();
    const existingIndex = polls.findIndex((p) => p.id === normalizedPoll.id);

    if (existingIndex >= 0) {
      polls[existingIndex] = normalizedPoll;
    } else {
      polls.push(normalizedPoll);
    }
    savePolls(polls);
    return null;
  }
}

export async function deletePollById(id: string): Promise<void> {
  // 1) Supabase est la source de vérité — on supprime d'abord là-bas et on
  //    surface l'erreur si la row n'a pas été matched (par ex. RLS bloque).
  //    Bug hunt nuit #18 (2026-05-27) : avant ce fix, le DELETE Supabase
  //    était fire-and-forget — quand la policy RLS DELETE manquait sur
  //    public.polls, l'appel renvoyait 204 avec 0 rows matched, le warn
  //    était logué silencieusement, et la row restait en DB pendant que
  //    localStorage et l'UI affichaient la suppression.
  //    Bug hunt nuit #19 (2026-05-28) : la policy DELETE seule ne suffisait
  //    pas — un `.from('polls').delete()` direct touche 0 ligne pour les
  //    sondages guest (pas de policy SELECT depuis le lockdown 2026-04-23 → la
  //    pré-phase SELECT du DELETE PostgREST ne voit aucune ligne). On passe
  //    par le RPC SECURITY DEFINER `delete_poll` qui bypass la RLS mais
  //    re-vérifie la propriété (auth.uid() OU guest_fingerprint), comme
  //    set_poll_status. Cf. 20260528_delete_poll_rpc.sql.
  let dbDeleted = false;
  try {
    const { supabase } = await import("./supabase");

    let callerFingerprint: string | null = null;
    try {
      const { getFingerprint } = await import("./guestIdentityService");
      callerFingerprint = await getFingerprint();
    } catch {
      // Fingerprint optionnel : null si indisponible. Le RPC laissera passer
      // les utilisateurs connectés et refusera les invités sans fingerprint.
    }

    const { getGuestOwnerToken } = await import("./guestIdentityService");
    const { data, error } = await supabase.rpc("delete_poll", {
      p_poll_id: id,
      p_fingerprint: callerFingerprint,
      p_owner_token: getGuestOwnerToken(),
    });

    if (error) {
      logger.error("Failed to delete poll from Supabase", "poll", {
        id,
        error,
      });
      throw new Error(`Supabase delete failed: ${error.message}`);
    }

    const deletedCount = typeof data === "number" ? data : Number(data ?? 0);
    if (deletedCount === 0) {
      logger.warn(
        "Supabase delete matched zero rows — RLS or missing row",
        "poll",
        { id },
      );
      throw new Error(
        "Supabase delete matched zero rows — vérifiez les droits sur ce sondage",
      );
    }

    dbDeleted = true;
    logger.info("Poll deleted from Supabase", "poll", { id });
  } catch (err) {
    // On garde localStorage en synchro avec le DB : si la DB n'a pas pu
    // supprimer, on ne purge pas non plus côté local.
    if (!dbDeleted) {
      throw err instanceof Error ? err : new Error(String(err));
    }
  }

  // 2) localStorage ne suit qu'après confirmation que la row a bien été
  //    supprimée côté DB.
  const polls = getPolls().filter((p) => p.id !== id);
  savePolls(polls);
  deleteFormResponsesForPoll(id);
  deleteVotesByPollId(id);
}

export async function duplicatePoll(poll: Poll): Promise<Poll> {
  const newPoll = {
    ...poll,
    id: `dup-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    title: `${poll.title} (Copie)`,
    slug: `${poll.slug}-copy-${Date.now()}`,
    status: "draft" as const,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };
  await addPoll(newPoll);
  return newPoll;
}

export function buildPublicLink(slug: string): string {
  if (typeof window === "undefined") return `/poll/${slug}/vote`;
  return `${window.location.origin}/poll/${slug}/vote`;
}

export async function copyToClipboard(text: string): Promise<void> {
  try {
    // Essayer d'abord l'API Clipboard moderne (nécessite contexte sécurisé + geste utilisateur)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    // Fallback pour les anciens navigateurs ou contextes non sécurisés
    const textArea = document.createElement("textarea");
    textArea.value = text;

    // Rendre l'élément invisible mais cliquable
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    // Utiliser execCommand comme fallback
    const successful = document.execCommand("copy");
    document.body.removeChild(textArea);

    if (!successful) {
      throw new Error("execCommand failed");
    }
  } catch (err) {
    logger.error("Failed to copy to clipboard", "poll", { err });
    throw new Error("Impossible de copier le lien dans le presse-papiers");
  }
}

// ============================================================================
// POLL RETRIEVAL & MIGRATION
// ============================================================================

export function getAllPolls(): Poll[] {
  if (typeof window !== "undefined") {
    const hasMigrated = (window as any)._doodates_migrated;
    if (!hasMigrated) {
      migrateFormDraftsIntoUnified();
      migrateCreatorIdsForGuestPolls();
      (window as any)._doodates_migrated = true;
    }
  }
  return getRawAllPolls();
}

export function migrateCreatorIdsForGuestPolls(): void {
  try {
    const currentDeviceId = getDeviceId();
    const polls = getRawAllPolls();
    let hasChanges = false;
    const migratedPolls = polls.map((poll) => {
      if (
        !poll.creator_id ||
        poll.creator_id === "anonymous" ||
        poll.creator_id === "local-guest"
      ) {
        hasChanges = true;
        return { ...poll, creator_id: currentDeviceId };
      }
      return poll;
    });
    if (hasChanges) {
      savePolls(migratedPolls);
      logger.info("Migrated guest poll creator_ids", "poll");
    }
  } catch (error) {
    logger.error("Failed to migrate creator_ids", "poll", { error });
  }
}

export function migrateFormDraftsIntoUnified(): void {
  if (!hasWindow()) return;
  try {
    const raw = window.localStorage.getItem(FORM_STORAGE_KEY);
    const legacyForms = raw
      ? (JSON.parse(raw) as Record<string, LegacyFormPoll>)
      : null;
    if (!legacyForms || Object.keys(legacyForms).length === 0) return;
    const polls = getRawAllPolls();
    let migrationCount = 0;
    Object.entries(legacyForms).forEach(([id, formData]) => {
      if (polls.some((p) => p.id === id)) return;
      const unifiedPoll: Poll = {
        id,
        type: "form",
        title: String(formData.title || "Formulaire importé"),
        slug: String(formData.slug || id),
        status: (formData.status as any) || "draft",
        creator_id: String(formData.creator_id || getDeviceId()),
        created_at: String(formData.created_at || new Date().toISOString()),
        updated_at: String(formData.updated_at || new Date().toISOString()),
        questions: (formData.questions || []) as FormQuestionShape[],
        settings: {
          questions: (formData.questions || []) as FormQuestionShape[],
        } as any,
      };
      polls.push(unifiedPoll);
      migrationCount++;
    });
    if (migrationCount > 0) {
      savePolls(polls);
      localStorage.removeItem(FORM_STORAGE_KEY);
      logger.info(`${migrationCount} legacy forms migrated`, "poll");
    }
  } catch (error) {
    logger.error("Failed to migrate legacy forms", "poll", { error });
  }
}

// ============================================================================
// FORM RESPONSES & RESULTS
// ============================================================================

const memoryResultsCache = new Map<string, FormResults>();

export function deleteFormResponsesForPoll(pollId: string): void {
  const all = readAllResponses();
  const filtered = all.filter((r) => r.pollId !== pollId);
  writeAllResponses(filtered);
  memoryResultsCache.delete(pollId);
}

export function getFormPollById(pollId: string): Poll | null {
  return getPolls().find((p) => p.id === pollId) || null;
}

/**
 * @deprecated SPRINT 2 form-polls launch (mai 2026, B7 ✅ mergé PR #654).
 *
 * 🚫 NE PAS APPELER pour du nouveau code. Écrit en localStorage uniquement
 *    (interdit par [Docs/ARCHITECTURE/2026-04-01-STORAGE-ARCHITECTURE-FINAL.md]).
 *
 * ✅ Utiliser : `VoteService.castVote({responseType: "form", ...})` (RPC
 *    SECURITY DEFINER `upsert_poll_response`).
 *
 * Reste exporté uniquement pour compatibilité legacy (data pré-migration).
 * Tous les call-sites form-polls actifs (FormPollVote, MultiStepFormVote,
 * FormPollResults, UserDataExportService) ont été migrés.
 */
export function addFormResponse(params: {
  pollId: string;
  respondentName?: string;
  respondentEmail?: string;
  items: FormResponseItem[];
}): FormResponse {
  const { pollId, respondentName, respondentEmail, items } = params;

  if (!pollId) {
    throw new DooDatesError(
      "pollId is required",
      "pollId est requis pour enregistrer une réponse",
      ErrorSeverity.MEDIUM,
      ErrorCategory.VALIDATION,
      { metadata: { params } },
    );
  }

  const response: FormResponse = {
    id: `resp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    pollId,
    respondentName,
    respondentEmail,
    items,
    deviceId: getDeviceId(),
    created_at: new Date().toISOString(),
  };

  const all = readAllResponses();
  all.push(response);
  writeAllResponses(all);
  memoryResultsCache.delete(pollId);

  return response;
}

/**
 * @deprecated SPRINT 2 form-polls launch (mai 2026, B7 ✅).
 *
 * 🚫 Lit en localStorage uniquement.
 *
 * ✅ Utiliser : `aggregateFormResults(pollId, responses, questions)` de
 *    `@core/lib/formResults`, alimenté par `VoteService.listByPollIdAndType`.
 *    Cf. `apps/form-polls/src/components/polls/FormPollResults.tsx` pour
 *    l'exemple de référence.
 *
 * Conservé pour data legacy localStorage uniquement.
 */
export function getFormResults(pollId: string): FormResults {
  if (memoryResultsCache.has(pollId)) return memoryResultsCache.get(pollId)!;

  const poll = getFormPollById(pollId);
  const all = getFormResponses(pollId);

  const countsByQuestion: Record<string, Record<string, number>> = {};
  const textAnswers: Record<string, string[]> = {};
  const dateResults: Record<string, DateQuestionResults> = {};

  const questions = poll?.questions || [];

  questions.forEach((q: FormQuestionShape) => {
    countsByQuestion[q.id] = {};
    textAnswers[q.id] = [];
  });

  all.forEach((resp) => {
    resp.items.forEach((item) => {
      const q = questions.find(
        (quest: FormQuestionShape) => quest.id === item.questionId,
      );
      if (!q) return;

      const kind = q.kind as string;
      if (kind === "text" || kind === "long-text") {
        if (item.value) textAnswers[q.id].push(String(item.value));
      } else if (kind === "single" || kind === "rating") {
        const val = String(item.value);
        if (!countsByQuestion[q.id]) countsByQuestion[q.id] = {};
        countsByQuestion[q.id][val] = (countsByQuestion[q.id][val] || 0) + 1;
      } else if (kind === "multiple") {
        const vals = Array.isArray(item.value) ? item.value : [item.value];
        vals.forEach((v) => {
          const val = String(v);
          if (!countsByQuestion[q.id]) countsByQuestion[q.id] = {};
          countsByQuestion[q.id][val] = (countsByQuestion[q.id][val] || 0) + 1;
        });
      }
    });
  });

  const finalResults: FormResults = {
    pollId,
    countsByQuestion,
    textAnswers,
    dateResults,
    totalResponses: all.length,
  };

  memoryResultsCache.set(pollId, finalResults);
  return finalResults;
}

/**
 * @deprecated SPRINT 1 form-polls launch (mai 2026) — lit en localStorage uniquement.
 *
 * ✅ Remplace par : `VoteService.listByPollIdAndType(pollId, "form")` dans
 *    `packages/core/src/services/VoteService.ts` (RPC `get_poll_responses`,
 *    SECURITY DEFINER, exclut les PII serveur-side).
 */
export function getFormResponses(pollId: string): FormResponse[] {
  return readAllResponses().filter((r) => r.pollId === pollId);
}

export function anonymizeFormResponsesForPoll(pollId: string): {
  anonymizedCount: number;
} {
  try {
    const all = readAllResponses();
    let anonymizedCount = 0;
    const updated = all.map((response) => {
      if (response.pollId !== pollId) return response;
      if (!response.respondentName && !response.respondentEmail)
        return response;
      anonymizedCount += 1;
      return {
        ...response,
        respondentName: undefined,
        respondentEmail: undefined,
      };
    });
    if (anonymizedCount > 0) writeAllResponses(updated);
    return { anonymizedCount };
  } catch (error) {
    logger.error("Error anonymizing form responses", "poll", { pollId, error });
    return { anonymizedCount: 0 };
  }
}

// ============================================================================
// VOTES & ANONYMIZATION
// ============================================================================

export function getVoterId(vote: any): string {
  const email = (vote?.voter_email || "").trim().toLowerCase();
  if (email) return email;
  const name = (vote?.voter_name || "").trim().toLowerCase();
  if (name) return `name:${name}`;
  return `anon:${vote?.id || Math.random().toString(36).slice(2, 8)}`;
}

export function getVotesByPollId(pollId: string): Vote[] {
  return getAllVotes().filter((vote) => vote.poll_id === pollId);
}

export function anonymizeVotesForPoll(pollId: string): {
  anonymizedCount: number;
} {
  try {
    const allVotes = getAllVotes();
    let anonymizedCount = 0;
    const updatedVotes = allVotes.map((vote) => {
      if (vote.poll_id !== pollId) return vote;
      if (!vote.voter_name && !vote.voter_email) return vote;
      anonymizedCount += 1;
      return { ...vote, voter_name: undefined, voter_email: undefined };
    });
    if (anonymizedCount > 0) saveVotes(updatedVotes);
    return { anonymizedCount };
  } catch (error) {
    logger.error("Error anonymizing votes", "poll", { pollId, error });
    return { anonymizedCount: 0 };
  }
}

export function deleteVotesByPollId(pollId: string): void {
  saveVotes(getAllVotes().filter((vote) => vote.poll_id !== pollId));
}

export function getRespondentId(resp: FormResponse): string {
  const name = (resp?.respondentName || "").trim().toLowerCase();
  if (name) return `name:${name}`;
  if (resp.deviceId) return `anon:${resp.deviceId}:${resp.id}`;
  const legacyId = (resp as any).respondentId;
  if (legacyId) return legacyId;
  return `anon:unknown:${resp.id}`;
}

export function checkIfUserHasVoted(pollId: string): boolean {
  const deviceId = getDeviceId();
  const responses = getFormResponses(pollId);
  return responses.some(
    (r) => r.deviceId === deviceId || getRespondentId(r).includes(deviceId),
  );
}

// ============================================================================
// CONVERSATION LINKING
// ============================================================================

export function getPollByConversationId(conversationId: string): Poll | null {
  return getAllPolls().find((p) => p.conversationId === conversationId) || null;
}

export function updatePollConversationLink(
  pollId: string,
  conversationId: string,
): void {
  const poll = getPollBySlugOrId(pollId);
  if (!poll) {
    throw new DooDatesError(
      `Poll not found: ${pollId}`,
      `Poll non trouvé: ${pollId}`,
      ErrorSeverity.HIGH,
      ErrorCategory.STORAGE,
      { pollId },
    );
  }
  addPoll({ ...poll, conversationId, updated_at: new Date().toISOString() });
}
